/**
 * Admin Competency Requests inbox + detail (Sprint TCO-B).
 *
 * HM Advisor / HM Admin surface for the requests submitted by Client Admin.
 * Lists all requests (advisor-scoped) and lets the HM resolve each one:
 *   • Case A → click Assign, confirm profile + due date, fan-out happens
 *     through the existing assignment engine.
 *   • Case B → HM creates the profile via the existing TCO-A catalog
 *     ("Go to catalog" CTA), waits for it to be published, then returns
 *     here and assigns by picking it from the published list.
 *
 * Reject path captures a reason. No new questionnaire / assignment / report
 * logic is created in this module.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Inbox, ArrowLeft, Send, X as XIcon, Search, BookOpen, AlertTriangle,
} from 'lucide-react';

import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

import { competencyRequestAdminApi, competencyProfileApi } from '@/lib/competencyProfileApi';
import { api, formatApiError } from '@/lib/api';
import { getUiLocale } from '@/lib/dates';

const STATUS_TONES = {
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  profile_available: 'bg-sky-50 text-sky-700 border-sky-200',
  assigned:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const STATUSES = ['pending', 'profile_available', 'assigned', 'rejected', 'cancelled'];

export default function AdminCompetencyRequests() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  // UAT-002 — notifications deep-link straight to the pending request (?rid=…)
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(searchParams.get('rid') || null);
  const [profiles, setProfiles] = useState([]);  // published profiles
  const [companyEmployees, setCompanyEmployees] = useState({});  // {company_id: [users]}

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDraft, setAssignDraft] = useState({
    competency_profile_id: '', employee_user_id: '', position: '', deadline: '', context_notes: '',
  });
  const [acting, setActing] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Founder UAT iter 3 — phase separation for new-profile requests
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [fulfillProfileId, setFulfillProfileId] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const [rs, ps] = await Promise.all([
        competencyRequestAdminApi.list(),
        competencyProfileApi.list({ status: 'published' }),
      ]);
      setRequests(rs);
      setProfiles(ps);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const visible = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return requests
      .filter((r) => statusFilter === 'all' || r.status === statusFilter)
      .filter((r) => !q
        || (r.proposed_title || '').toLowerCase().includes(q)
        || (r.position || '').toLowerCase().includes(q)
        || (r.requested_by_name || '').toLowerCase().includes(q)
        || (r.requested_by_email || '').toLowerCase().includes(q));
  }, [requests, statusFilter, search]);

  const counts = useMemo(() => {
    const out = { all: requests.length };
    STATUSES.forEach((s) => { out[s] = requests.filter((r) => r.status === s).length; });
    return out;
  }, [requests]);

  const selected = useMemo(
    () => requests.find((r) => r.id === selectedId) || null,
    [requests, selectedId],
  );

  // Lazy-load employees for the selected request's company.
  useEffect(() => {
    if (!selected) return;
    const cid = selected.company_id;
    if (!cid || companyEmployees[cid]) return;
    api.get('/admin/users', { params: { company_id: cid } })
      .then((r) => {
        const evals = (r.data || []).filter((u) => u.role === 'evaluated');
        setCompanyEmployees((m) => ({ ...m, [cid]: evals }));
      })
      .catch(() => { /* fall back to placeholder */ });
  }, [selected, companyEmployees]);

  const openAssign = () => {
    if (!selected) return;
    setAssignDraft({
      competency_profile_id: selected.competency_profile_id || '',
      employee_user_id: selected.employee_user_id || '',
      position: selected.position || selected.proposed_title || '',
      deadline: '',
      context_notes: '',
    });
    setAssignOpen(true);
  };

  const runAssign = async () => {
    if (!selected) return;
    if (!assignDraft.competency_profile_id) return toast.error(t('tco.adminReq.errors.profile', { defaultValue: 'Pick a competency profile.' }));
    if (!assignDraft.employee_user_id) return toast.error(t('tco.adminReq.errors.employee', { defaultValue: 'Pick an employee.' }));
    setActing(true);
    try {
      const body = {
        competency_profile_id: assignDraft.competency_profile_id,
        employee_user_id: assignDraft.employee_user_id,
        position: assignDraft.position || null,
        deadline: assignDraft.deadline ? new Date(assignDraft.deadline).toISOString() : null,
        context_notes: assignDraft.context_notes || null,
      };
      const out = await competencyRequestAdminApi.assign(selected.id, body);
      toast.success(t('tco.adminReq.assignedToast', {
        defaultValue: 'Assigned · {{n}} assessment(s) sent',
        n: (out.assignment_ids || []).length,
      }));
      setAssignOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const runFulfill = async () => {
    if (!selected) return;
    if (!fulfillProfileId) return toast.error(t('tco.adminReq.errors.profile', { defaultValue: 'Selecciona un perfil publicado.' }));
    setActing(true);
    try {
      await competencyRequestAdminApi.fulfill(selected.id, fulfillProfileId);
      toast.success(t('tco.adminReq.fulfilledToast', { defaultValue: 'Perfil disponible — el cliente ha sido notificado' }));
      setFulfillOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const runReject = async () => {
    if (!selected) return;
    if (!rejectReason.trim()) return toast.error(t('tco.adminReq.errors.reason', { defaultValue: 'A reason is required.' }));
    setActing(true);
    try {
      await competencyRequestAdminApi.reject(selected.id, rejectReason.trim());
      toast.success(t('tco.adminReq.rejectedToast', { defaultValue: 'Request rejected' }));
      setRejectOpen(false);
      setRejectReason('');
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const employeesForSelected = (selected && companyEmployees[selected.company_id]) || [];
  const employeeNameById = (uid) => {
    if (!uid) return '—';
    const e = employeesForSelected.find((x) => x.id === uid);
    return e ? `${e.name} (${e.email})` : uid;
  };

  if (selected) {
    return (
      <>
        <Topbar
          title={selected.type === 'new_profile'
            ? t('tco.adminReq.detailTitleJobProfile', { defaultValue: 'Job Profile request' })
            : t('tco.adminReq.detailTitle', { defaultValue: 'Competency request' })}
          subtitle={selected.requested_by_name + ' · ' + (selected.requested_by_email || '')}
          actions={
            <Button variant="outline" onClick={() => setSelectedId(null)} data-testid="tco-adminreq-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
          }
        />
        <div className="p-6 lg:p-8 space-y-6" data-testid={`tco-adminreq-detail-${selected.id}`}>
          <Card className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`text-xs ${STATUS_TONES[selected.status] || ''}`} data-testid="tco-adminreq-detail-status">
                {t(`tco.adminReq.status.${selected.status}`, { defaultValue: selected.status })}
              </Badge>
              <Badge variant="outline" className="text-xs" data-testid="tco-adminreq-detail-type">
                {t(`tco.adminReq.type.${selected.type}`, { defaultValue: selected.type })}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {t('tco.adminReq.requestedAt', { defaultValue: 'Requested' })}: {new Date(selected.requested_at).toLocaleString(getUiLocale())}
              </span>
            </div>

            {selected.type === 'existing_profile' && selected.competency_profile_id && (
              <Row label={t('tco.adminReq.profile', { defaultValue: 'Profile' })}>
                {profiles.find((p) => p.id === selected.competency_profile_id)?.name || selected.competency_profile_id}
              </Row>
            )}
            {selected.employee_user_id && (
              <Row label={t('tco.adminReq.employee', { defaultValue: 'Employee' })}>
                {employeeNameById(selected.employee_user_id)}
              </Row>
            )}
            {selected.position && (
              <Row label={t('tco.adminReq.position', { defaultValue: 'Position' })}>{selected.position}</Row>
            )}

            {selected.type === 'new_profile' && (
              <>
                <Row label={t('tco.adminReq.proposedTitle', { defaultValue: 'Proposed title' })}>{selected.proposed_title}</Row>
                {selected.proposed_department && (
                  <Row label={t('tco.adminReq.proposedDepartment', { defaultValue: 'Department' })}>{selected.proposed_department}</Row>
                )}
                {selected.proposed_description && (
                  <Row label={t('tco.adminReq.proposedDescription', { defaultValue: 'Description' })}>{selected.proposed_description}</Row>
                )}
                {selected.business_justification && (
                  <Row label={t('tco.adminReq.justification', { defaultValue: 'Business justification' })}>{selected.business_justification}</Row>
                )}
              </>
            )}
            {selected.notes && <Row label={t('tco.adminReq.notes', { defaultValue: 'Client notes' })}>{selected.notes}</Row>}
          </Card>

          {/* Founder UAT iter 3 — new-profile lifecycle: explicit phases */}
          {selected.type === 'new_profile' && (
            <Card className="p-5" data-testid="tco-adminreq-phases">
              <h3 className="font-semibold mb-3">{t('tco.adminReq.phasesTitle', { defaultValue: 'Ciclo de vida de la solicitud' })}</h3>
              <ol className="space-y-2 text-sm">
                <PhaseRow done label={t('tco.adminReq.phase1', { defaultValue: 'Fase 1 — El cliente solicita un perfil de competencias' })} />
                <PhaseRow
                  done={selected.status === 'profile_available'}
                  active={selected.status === 'pending'}
                  label={t('tco.adminReq.phase2', { defaultValue: 'Fase 2 — CTH crea y publica el perfil en el catálogo' })}
                />
                <PhaseRow
                  done={selected.status === 'profile_available'}
                  label={t('tco.adminReq.phase3', { defaultValue: '✔ Perfil de competencias disponible (la solicitud queda completada y el cliente es notificado)' })}
                />
              </ol>
              <p className="text-xs text-muted-foreground mt-3">
                {t('tco.adminReq.phasesNote', {
                  defaultValue: 'Crear y asignar evaluaciones TalentCompetency es un flujo independiente que ocurre DESPUÉS, usando el perfil publicado. Esta solicitud no asigna evaluaciones.',
                })}
              </p>
            </Card>
          )}

          {/* Case B helper card — FIX 1: once a profile is linked, the request
              RESUMES it; it never restarts the creation flow. */}
          {selected.type === 'new_profile' && selected.status === 'pending' && (
            selected.linked_profile ? (
              <Card className="p-5 bg-emerald-50/40 border-emerald-200" data-testid="tco-adminreq-linked-profile-card">
                <div className="flex items-start gap-3">
                  <BookOpen className="h-5 w-5 text-emerald-700 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold">{t('tco.adminReq.linkedProfileTitle', { defaultValue: 'Perfil vinculado' })}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="font-medium text-foreground">{selected.linked_profile.name}</span>
                      {selected.linked_profile.department ? ` · ${selected.linked_profile.department}` : ''}
                      {' · '}
                      {t(`tco.status.${selected.linked_profile.status}`, { defaultValue: selected.linked_profile.status })}
                      {' · '}
                      {t('tco.competenciesCount', { defaultValue: '{{n}} competencies', n: selected.linked_profile.competencies_count })}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('tco.adminReq.linkedProfileHint', {
                        defaultValue: 'Esta solicitud ya tiene un perfil en curso. Continúa el trabajo guardado — no se creará otro perfil.',
                      })}
                    </p>
                    <div className="mt-3">
                      <Button size="sm"
                              onClick={() => navigate(`/admin/talentcompetency/profiles/${selected.linked_profile.id}`)}
                              data-testid="tco-adminreq-continue-profile-btn">
                        <BookOpen className="h-4 w-4 mr-1.5" />
                        {t('tco.adminReq.continueProfile', { defaultValue: 'Continuar perfil' })}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
            <Card className="p-5 bg-amber-50/40" data-testid="tco-adminreq-case-b-helper">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold">{t('tco.adminReq.newProfileNeeded', { defaultValue: 'Profile creation needed' })}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('tco.adminReq.newProfileBodyV2', {
                      defaultValue: 'Crea el perfil en el catálogo (quedará vinculado a esta solicitud) y publícalo. Al publicarse, la solicitud se completará automáticamente como “Perfil disponible”. Si el perfil ya existe, márcalo disponible manualmente.',
                    })}
                  </p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm"
                            onClick={() => navigate(`/admin/talentcompetency?fromRequest=${selected.id}&title=${encodeURIComponent(selected.proposed_title || '')}`)}
                            data-testid="tco-adminreq-open-catalog-btn">
                      <BookOpen className="h-4 w-4 mr-1.5" />
                      {t('tco.adminReq.createLinkedProfile', { defaultValue: 'Crear perfil vinculado' })}
                    </Button>
                    <Button size="sm" onClick={() => { setFulfillProfileId(''); setFulfillOpen(true); }}
                            data-testid="tco-adminreq-fulfill-btn">
                      <Send className="h-4 w-4 mr-1.5" />
                      {t('tco.adminReq.markAvailable', { defaultValue: 'Marcar perfil disponible' })}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
            )
          )}

          {/* Action bar — assign only applies to existing-profile (Case A) requests */}
          {selected.status === 'pending' && (
            <Card className="p-5 space-y-3">
              {selected.type === 'existing_profile' && (
                <p className="text-xs text-muted-foreground" data-testid="tco-adminreq-assign-explainer">
                  {t('tco.adminReq.assignExplainer', {
                    defaultValue: 'Este es un flujo de EVALUACIÓN: al asignar, se envían al colaborador los cuestionarios vinculados al perfil publicado seleccionado. No crea ni modifica perfiles.',
                  })}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {selected.type === 'existing_profile' && (
                  <Button onClick={openAssign} data-testid="tco-adminreq-assign-btn">
                    <Send className="h-4 w-4 mr-1.5" />
                    {t('tco.adminReq.assignEvaluation', { defaultValue: 'Asignar evaluación al colaborador' })}
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setRejectReason(''); setRejectOpen(true); }} data-testid="tco-adminreq-reject-btn">
                  <XIcon className="h-4 w-4 mr-1.5" />
                  {t('tco.adminReq.reject', { defaultValue: 'Reject' })}
                </Button>
              </div>
            </Card>
          )}

          {/* Outcome card */}
          {selected.status === 'profile_available' && (
            <Card className="p-5 bg-sky-50/40" data-testid="tco-adminreq-outcome-available">
              <h3 className="font-semibold">{t('tco.adminReq.availableHeader', { defaultValue: '✔ Perfil de competencias disponible' })}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('tco.adminReq.availableBody', {
                  defaultValue: 'El perfil “{{name}}” está publicado y el cliente fue notificado. La solicitud está completada. Las evaluaciones con este perfil se crean en un flujo independiente.',
                  name: selected.fulfilled_profile_name || '',
                })}
              </p>
            </Card>
          )}
          {selected.status === 'assigned' && (
            <Card className="p-5 bg-emerald-50/40" data-testid="tco-adminreq-outcome-assigned">
              <h3 className="font-semibold">{t('tco.adminReq.assignedHeader', { defaultValue: 'Assignments created' })}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('tco.adminReq.assignedBody', {
                  defaultValue: '{{n}} assessment(s) dispatched via the existing assignment engine.',
                  n: (selected.assigned_assignment_ids || []).length,
                })}
              </p>
            </Card>
          )}

          {/* TC-Closeout FIX 7 — downstream execution visibility */}
          {selected.execution && (
            <Card className="p-5" data-testid="tco-adminreq-execution-card">
              <h3 className="font-semibold">{t('tco.adminReq.executionTitle', { defaultValue: 'Ejecución de la evaluación' })}</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                {t('tco.adminReq.executionSubtitle', { defaultValue: 'Estado operativo posterior a la asignación — la solicitud conserva su registro de gobernanza.' })}
              </p>
              <ul className="divide-y">
                {selected.execution.assignments.map((ev) => (
                  <li key={ev.assignment_id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`tco-adminreq-exec-row-${ev.assignment_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{ev.participant_name || '—'}</span>
                        <Badge variant="outline" className="text-xs" data-testid={`tco-adminreq-exec-stage-${ev.assignment_id}`}>
                          {t(`tco.stage.${ev.stage}`, { defaultValue: ev.stage })}
                        </Badge>
                        {ev.report_status && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            {t(`reports.review.statusLabel.${ev.report_status}`, { defaultValue: ev.report_status })}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ev.position || ev.title || '—'}
                        {ev.submitted_at ? ` · ${new Date(ev.submitted_at).toLocaleString(getUiLocale())}` : ''}
                      </p>
                    </div>
                    {ev.report_id && (
                      <Button size="sm" variant="outline" className="shrink-0"
                              onClick={() => navigate(`/admin/reports/${ev.report_id}`)}
                              data-testid={`tco-adminreq-exec-report-btn-${ev.assignment_id}`}>
                        {t('tco.adminReq.openReport', { defaultValue: 'Abrir informe' })}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {selected.status === 'rejected' && selected.rejected_reason && (
            <Card className="p-5 bg-rose-50/40" data-testid="tco-adminreq-outcome-rejected">
              <h3 className="font-semibold">{t('tco.adminReq.rejectedHeader', { defaultValue: 'Request rejected' })}</h3>
              <p className="text-sm text-muted-foreground mt-1">{selected.rejected_reason}</p>
            </Card>
          )}

          {/* Lifecycle history */}
          {(selected.history || []).length > 0 && (
            <Card className="p-5 space-y-2" data-testid="tco-adminreq-history-card">
              <h3 className="font-semibold">{t('tco.adminReq.history', { defaultValue: 'History' })}</h3>
              <ul className="space-y-2 text-sm">
                {selected.history.map((h, idx) => (
                  <li key={idx} className="flex items-start gap-2" data-testid={`tco-adminreq-history-row-${idx}`}>
                    <Badge variant="outline" className="text-xs">{t(`tco.adminReq.status.${h.from_status}`, { defaultValue: h.from_status })} → {t(`tco.adminReq.status.${h.to_status}`, { defaultValue: h.to_status })}</Badge>
                    <span className="text-muted-foreground">
                      {h.by_user_name || h.by_user_id} ({h.by_role}) · {new Date(h.at).toLocaleString(getUiLocale())}
                    </span>
                    {h.note && <span className="italic">— {h.note}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Assign dialog — FIX 5: responsive, no horizontal overflow */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="w-[min(94vw,560px)] sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="tco-adminreq-assign-dialog">
            <DialogHeader>
              <DialogTitle>{t('tco.adminReq.assignTitleV2', { defaultValue: 'Asignar evaluación de competencias' })}</DialogTitle>
              <DialogDescription>
                {t('tco.adminReq.assignDescV2', {
                  defaultValue: 'Acción: enviar EVALUACIONES al colaborador. Se crean las asignaciones de los cuestionarios vinculados al perfil publicado elegido. No modifica el perfil.',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 min-w-0">
              <div className="min-w-0">
                <Label htmlFor="tco-assign-profile">{t('tco.adminReq.profile', { defaultValue: 'Profile' })} <span className="text-destructive">*</span></Label>
                <Select
                  value={assignDraft.competency_profile_id}
                  onValueChange={(v) => setAssignDraft((d) => ({ ...d, competency_profile_id: v }))}
                >
                  <SelectTrigger id="tco-assign-profile" className="w-full min-w-0 [&>span]:truncate [&>span]:text-left" data-testid="tco-adminreq-assign-profile">
                    <SelectValue placeholder={t('tco.adminReq.pickProfile', { defaultValue: 'Pick a published profile…' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.length === 0 ? (
                      <SelectItem disabled value="__none__">{t('tco.adminReq.noPublishedProfiles', { defaultValue: 'No published profiles yet. Build one in the catalog first.' })}</SelectItem>
                    ) : profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`tco-adminreq-assign-profile-opt-${p.id}`}>
                        {p.name}{p.department ? ` · ${p.department}` : ''}
                        {(p.linked_questionnaire_ids?.length ?? 0) > 0 ? ` (${p.linked_questionnaire_ids.length} assessments)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tco-assign-employee">{t('tco.adminReq.employee', { defaultValue: 'Employee' })} <span className="text-destructive">*</span></Label>
                <Select
                  value={assignDraft.employee_user_id}
                  onValueChange={(v) => setAssignDraft((d) => ({ ...d, employee_user_id: v }))}
                >
                  <SelectTrigger id="tco-assign-employee" className="w-full min-w-0 [&>span]:truncate [&>span]:text-left" data-testid="tco-adminreq-assign-employee">
                    <SelectValue placeholder={t('tco.adminReq.pickEmployee', { defaultValue: 'Pick an employee…' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {employeesForSelected.length === 0 ? (
                      <SelectItem disabled value="__none__">{t('tco.adminReq.noEmployees', { defaultValue: 'No evaluated users in this company' })}</SelectItem>
                    ) : employeesForSelected.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`tco-adminreq-assign-employee-opt-${e.id}`}>
                        {e.name} — {e.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tco-assign-position">{t('tco.adminReq.position', { defaultValue: 'Position (optional)' })}</Label>
                <Input
                  id="tco-assign-position"
                  value={assignDraft.position}
                  onChange={(e) => setAssignDraft((d) => ({ ...d, position: e.target.value }))}
                  data-testid="tco-adminreq-assign-position"
                />
              </div>
              <div>
                <Label htmlFor="tco-assign-deadline">{t('tco.adminReq.deadline', { defaultValue: 'Deadline (optional)' })}</Label>
                <Input
                  id="tco-assign-deadline"
                  type="date"
                  value={assignDraft.deadline}
                  onChange={(e) => setAssignDraft((d) => ({ ...d, deadline: e.target.value }))}
                  data-testid="tco-adminreq-assign-deadline"
                />
              </div>
              <div>
                <Label htmlFor="tco-assign-notes">{t('tco.adminReq.contextNotes', { defaultValue: 'Notes for the candidate (optional)' })}</Label>
                <Textarea
                  id="tco-assign-notes"
                  rows={2}
                  value={assignDraft.context_notes}
                  onChange={(e) => setAssignDraft((d) => ({ ...d, context_notes: e.target.value }))}
                  data-testid="tco-adminreq-assign-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="tco-adminreq-assign-cancel-btn">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={runAssign} disabled={acting} data-testid="tco-adminreq-assign-confirm-btn">
                {acting ? t('common.working', { defaultValue: 'Working…' }) : t('tco.adminReq.assign', { defaultValue: 'Assign' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fulfill dialog — mark requested profile as available */}
        <Dialog open={fulfillOpen} onOpenChange={setFulfillOpen}>
          <DialogContent data-testid="tco-adminreq-fulfill-dialog">
            <DialogHeader>
              <DialogTitle>{t('tco.adminReq.fulfillTitle', { defaultValue: 'Marcar perfil disponible' })}</DialogTitle>
              <DialogDescription>
                {t('tco.adminReq.fulfillDesc', {
                  defaultValue: 'Selecciona el perfil PUBLICADO que responde a esta solicitud. El cliente será notificado de que su perfil ya está disponible.',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="tco-fulfill-profile">{t('tco.adminReq.profile', { defaultValue: 'Profile' })} <span className="text-destructive">*</span></Label>
              <Select value={fulfillProfileId} onValueChange={setFulfillProfileId}>
                <SelectTrigger id="tco-fulfill-profile" data-testid="tco-adminreq-fulfill-profile">
                  <SelectValue placeholder={t('tco.adminReq.pickProfile', { defaultValue: 'Pick a published profile…' })} />
                </SelectTrigger>
                <SelectContent>
                  {profiles.length === 0 ? (
                    <SelectItem disabled value="__none__">{t('tco.adminReq.noPublishedProfiles', { defaultValue: 'No published profiles yet. Build one in the catalog first.' })}</SelectItem>
                  ) : profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`tco-adminreq-fulfill-profile-opt-${p.id}`}>
                      {p.name}{p.department ? ` · ${p.department}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFulfillOpen(false)} data-testid="tco-adminreq-fulfill-cancel-btn">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={runFulfill} disabled={acting || !fulfillProfileId} data-testid="tco-adminreq-fulfill-confirm-btn">
                {acting ? t('common.working', { defaultValue: 'Working…' }) : t('tco.adminReq.markAvailable', { defaultValue: 'Marcar perfil disponible' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent data-testid="tco-adminreq-reject-dialog">
            <DialogHeader>
              <DialogTitle>{t('tco.adminReq.rejectTitle', { defaultValue: 'Reject request' })}</DialogTitle>
              <DialogDescription>
                {t('tco.adminReq.rejectDesc', { defaultValue: 'Tell the client why this request was rejected.' })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="tco-reject-reason">{t('tco.adminReq.reason', { defaultValue: 'Reason' })} <span className="text-destructive">*</span></Label>
              <Textarea
                id="tco-reject-reason"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="tco-adminreq-reject-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="tco-adminreq-reject-cancel-btn">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={runReject} disabled={acting || !rejectReason.trim()} data-testid="tco-adminreq-reject-confirm-btn">
                {acting ? t('common.working', { defaultValue: 'Working…' }) : t('tco.adminReq.reject', { defaultValue: 'Reject' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // -------- Inbox view --------
  return (
    <>
      <Topbar
        title={t('tco.adminReq.title', { defaultValue: 'Competency requests' })}
        subtitle={t('tco.adminReq.subtitle', { defaultValue: 'Operational queue from Client Admins · resolve via the existing assignment engine.' })}
        actions={
          <Button variant="outline" onClick={() => navigate('/admin/talentcompetency')} data-testid="tco-adminreq-catalog-btn">
            <BookOpen className="h-4 w-4 mr-1.5" />
            {t('tco.adminReq.catalog', { defaultValue: 'Profile catalog' })}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="tco-adminreq-inbox">
        <Card className="p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Inbox className="h-4 w-4" />
              {t('tco.adminReq.inboxHeader', { defaultValue: 'Inbox · {{n}} requests', n: counts.all })}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8 h-9 w-64"
                  placeholder={t('tco.adminReq.searchPh', { defaultValue: 'Search by client, position…' })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="tco-adminreq-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-44" data-testid="tco-adminreq-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('tco.adminReq.statusAll', { defaultValue: 'All statuses' })} ({counts.all})
                  </SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`tco-adminreq-status-${s}`}>
                      {t(`tco.adminReq.status.${s}`, { defaultValue: s })} ({counts[s] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center" data-testid="tco-adminreq-empty">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
              <p className="text-sm text-muted-foreground">
                {requests.length === 0
                  ? t('tco.adminReq.emptyAll', { defaultValue: 'No competency requests yet.' })
                  : t('tco.adminReq.emptyFiltered', { defaultValue: 'No requests match the current filters.' })}
              </p>
            </div>
          ) : (
            <ul className="divide-y" data-testid="tco-adminreq-list">
              {visible.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className="w-full text-left px-5 py-4 hover:bg-accent/40 transition flex items-start gap-3"
                    data-testid={`tco-adminreq-row-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">
                          {r.type === 'existing_profile'
                            ? (profiles.find((p) => p.id === r.competency_profile_id)?.name || r.competency_profile_id)
                            : r.proposed_title || '(new profile request)'}
                        </span>
                        <Badge variant="outline" className={`text-xs ${STATUS_TONES[r.status] || ''}`} data-testid={`tco-adminreq-row-status-${r.id}`}>
                          {t(`tco.adminReq.status.${r.status}`, { defaultValue: r.status })}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {t(`tco.adminReq.type.${r.type}`, { defaultValue: r.type })}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        <span>{t('tco.adminReq.from', { defaultValue: 'From' })}: {r.requested_by_name || r.requested_by_email}</span>
                        {r.position && <span>{r.position}</span>}
                        <span>{new Date(r.requested_at).toLocaleString(getUiLocale())}</span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="col-span-2 whitespace-pre-line">{children}</span>
    </div>
  );
}

function PhaseRow({ done, active, label }) {
  return (
    <li className="flex items-start gap-2">
      <span className={`mt-0.5 h-4 w-4 rounded-full shrink-0 inline-flex items-center justify-center text-xs font-bold
        ${done ? 'bg-emerald-500 text-white' : active ? 'bg-amber-400 text-white animate-pulse' : 'bg-muted text-muted-foreground'}`}>
        {done ? '✓' : ''}
      </span>
      <span className={done ? 'text-foreground' : active ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
    </li>
  );
}
